import { FC } from 'react';
import { RadioField } from 'components/Forms/RadioField';
import { TextField } from 'components/Forms/TextField';
import { MultiSelectField, Option } from 'components/Forms/MultiSelectField';
import { useFieldValue } from 'hooks/useFormHelpers';
import { CriteriaType, RecipientSelectionMode } from 'store/shoppingCart/types';
import {
  CRITERIA_OPTIONS,
  DEFAULT_MAX_RESULTS,
  MAX_MAX_RESULTS,
  MIN_MAX_RESULTS,
  RECIPIENT_SELECTION_MODE_OPTIONS,
  TYPE_FILTER_OPTIONS,
} from './constants';
import { formatTypeFilters, parseTypeFilters } from './typeFilters';

const parseMaxResults = (value: string) => (value === '' ? null : parseInt(value, 10));

const validateMaxResults = (value: number | null) =>
  value != null && (value < MIN_MAX_RESULTS || value > MAX_MAX_RESULTS)
    ? `Enter a number from ${MIN_MAX_RESULTS} to ${MAX_MAX_RESULTS}.`
    : undefined;

// MultiSelectField's format/parse are typed Option[] -> Option[] (it has no separate
// stored-value generic); this bridges to the real CriteriaType[] the payload needs.
const formatCriteria = ((value: CriteriaType[] = []) =>
  CRITERIA_OPTIONS.filter(option => value.includes(option.value as CriteriaType))) as unknown as (
  value: Option[],
  name: string
) => Option[];

const parseCriteria = ((options: Option[] = []) =>
  options.map(option => option.value as CriteriaType)) as unknown as (
  value: Option[],
  name: string
) => Option[];

export const ShoppingCartSettingsSection: FC = () => {
  const recipientSelectionMode = useFieldValue('recipient_selection_mode') as
    | RecipientSelectionMode
    | undefined;

  return (
    <>
      <span className="oath-eyebrow inkBlue">Recipient Selection</span>
      <div className="row">
        <div className="col-md-6 padding0">
          <RadioField
            name="recipient_selection_mode"
            label="Shopping Cart Type:*"
            options={RECIPIENT_SELECTION_MODE_OPTIONS}
          />
        </div>
        <div className="col-md-4 padding0">
          <TextField
            name="max_results"
            label={`Max Results (Default: ${DEFAULT_MAX_RESULTS})`}
            type="number"
            parse={parseMaxResults}
            validate={validateMaxResults}
          />
        </div>
      </div>
      {recipientSelectionMode === RecipientSelectionMode.CRITERIA_MATCH ? (
        <div className="shopping-cart-criteria-select">
          <MultiSelectField
            name="criteria"
            label="Recipient types to include:*"
            placeholder="Select recipient types"
            options={CRITERIA_OPTIONS}
            format={formatCriteria}
            parse={parseCriteria}
            validate={value => (!value?.length ? 'This field is required.' : undefined)}
            handleChange={() => undefined}
          />
        </div>
      ) : (
        <p className="oath-bodysmall color-gray">
          You will be able to add Recipients via a Search bar on Step 2
        </p>
      )}
      <div className="shopping-cart-criteria-select">
        <MultiSelectField
          name="visible_type_filters"
          label="Visible Type filters"
          placeholder="All options"
          options={TYPE_FILTER_OPTIONS}
          format={formatTypeFilters}
          parse={parseTypeFilters}
          closeMenuOnSelect={false}
          handleChange={() => undefined}
        />
        <p className="oath-bodysmall color-gray">Leave empty to show all options.</p>
      </div>
    </>
  );
};
